import { memo, useContext, useEffect, useState, useMemo } from "react";
import { fetchMerchandisingApi } from "app/pages/Merchandising/merchandisingActionCreators";
import PropTypes from "prop-types";
import AppGlobalsContext from "app/AppGlobalsContext";
import { computedProductDates, sortProducts } from "app/utils/utils";
import ProductListBlock from "app/pages/.shared/DynamicBlocks/blocks/ProductListBlock/ProductListBlock";
import { fetchProductsApi } from "app/pages/Listing/productActionCreators";
import { useIntl } from "react-intl";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { stringify } from "qs";
import { shouldHideMerchProductPriceBanner } from "app/pages/Merchandising/merchandisingSelector";
import { FilterProvider, OPTION_MODE } from "app/features/product/productFilters";
import {
	PRODUCTS_FILTERS_KEY,
	PRODUCT_EXPERIENCES,
	PRODUCT_TAG_CODES,
	MULTI_STAGE_JOURNEY_OPTIONS,
	OFFER_TYPE_LABELS,
} from "app/constants";
import { SortProvider } from "app/features/product/productSort";

const MAX_PRODUCTS = 3;

const CUSTOM_COMPARATORS = {
	recommendation: (a, b) => (a?.rank || 0) - (b?.rank || 0),
	price_asc: (a, b) => (a?.fromPriceType?.value ?? 0) - (b?.fromPriceType?.value ?? 0),
	price_desc: (a, b) => (b?.fromPriceType?.value ?? 0) - (a?.fromPriceType?.value ?? 0),
};

const ProductListBlockContainer = props => {
	const { merchCode, shortVersion, shortVersionFeaturedProductUris = [] } = props;
	const [products, setProducts] = useState([]);
	const { formatMessage, formatDate } = useIntl();

	const [shouldHideProductPriceBanner, setHideProductPriceBanner] = useState(false);

	const [title, setTitle] = useState(formatMessage({ id: "products.carousel.listing.title" }));
	const { shop, partnerCode, productsVisibility, resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const redirectUrl = merchCode
		? {
				pathname: "/merch",
				search: `?${stringify({ code: merchCode })}`,
		  }
		: {
				pathname: "/listing",
		  };

	useEffect(() => {
		if (merchCode) {
			fetchMerchandisingApi({ code: merchCode, shop, productsVisibility, partnerCode }).then(
				response => {
					setTitle(response?.data?.merchandising?.label);

					const merchProductList = computedProductDates(response?.data?.products);
					let merchCatalogProductList = response?.data?.catalogProducts || [];
					merchCatalogProductList = computedProductDates(merchCatalogProductList);
					const finalProductList = merchProductList.concat(merchCatalogProductList);

					setProducts(finalProductList);
					setHideProductPriceBanner(
						shouldHideMerchProductPriceBanner(response?.data?.merchandising)
					);
				}
			);
		} else {
			fetchProductsApi({ shop, productsVisibility, partnerCode })
				.then(response => {
					let newProductList = computedProductDates(response?.data?.products);

					setProducts(newProductList);
				})
				.catch(error => {
					// eslint-disable-next-line no-console
					console.error(error);
				});
		}
	}, [merchCode, shop, productsVisibility, partnerCode, shortVersion]);

	let finalProductList = [...products];

	if (shortVersion) {
		if (shortVersionFeaturedProductUris?.length > 0) {
			const shortVersionFeaturedProducts = products.filter(product =>
				shortVersionFeaturedProductUris.includes(product.uri)
			);

			if (shortVersionFeaturedProducts?.length > 0) {
				finalProductList = sortProducts({
					products: shortVersionFeaturedProducts,
				}).slice(0, MAX_PRODUCTS);
			} else {
				finalProductList = products.slice(0, MAX_PRODUCTS);
			}
		} else {
			finalProductList = products.slice(0, MAX_PRODUCTS);
		}
	}

	const shouldShowMultiStageJourneyOption = finalProductList?.some(product =>
		[
			PRODUCT_EXPERIENCES.CIRCUIT,
			PRODUCT_EXPERIENCES.AUTOTOUR,
			PRODUCT_EXPERIENCES.CRUISE,
		].includes(product?.productExperience)
	);

	const facetConfigs = useMemo(
		() => [
			{
				facetId: PRODUCTS_FILTERS_KEY.DESTINATION,
				facetTitle: formatMessage({ id: "product.page.filter.destinations.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.DESTINATION,
						title: formatMessage({ id: "product.page.filter.destinations.label" }),
						valueGetter: item => item.country,

						...(shouldShowMultiStageJourneyOption && {
							buildOptions: () => [
								{
									value: "multi-stage-journey",
									label: formatMessage({
										id: "listing.filter.destination.multi.destinations.label",
									}),
								},
							],
							customMatcher: product =>
								[
									PRODUCT_EXPERIENCES.CIRCUIT,
									PRODUCT_EXPERIENCES.AUTOTOUR,
									PRODUCT_EXPERIENCES.CRUISE,
								].includes(product?.productExperience),
						}),

						allowSearch: true,
						search: { placeholderKey: "filter.destination.searchbar.placeholder" },
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.OFFER_TYPE,
				facetTitle: formatMessage({ id: "product.page.filter.offer.type.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.OFFER_TYPE,
						title: formatMessage({ id: "product.page.filter.offer.type.label" }),
						valueGetter: product => product.merchandisings,
						buildOptions: products => {
							const offerTypes = [
								...new Set(
									products
										.flatMap(product => product.merchandisings)
										.map(merchandising => {
											if (
												merchandising?.startsWith(
													PRODUCT_TAG_CODES.COUP_DE_COEUR
												)
											) {
												return PRODUCT_TAG_CODES.COUP_DE_COEUR;
											}
											if (merchandising?.startsWith(PRODUCT_TAG_CODES.GC)) {
												return PRODUCT_TAG_CODES.GC;
											}
											return null;
										})
										.filter(Boolean)
								),
							];

							return offerTypes.map(merchandising => ({
								value: merchandising,
								labelKey: OFFER_TYPE_LABELS[merchandising] ?? merchandising,
							}));
						},
						customMatcher: (product, selectedValues) => {
							const productOfferTypes = product.merchandisings;

							return selectedValues?.some(selectedValue => {
								if (selectedValue === PRODUCT_TAG_CODES.COUP_DE_COEUR) {
									const hasCDC = productOfferTypes?.some(offerType =>
										offerType.includes(PRODUCT_TAG_CODES.COUP_DE_COEUR)
									);
									const hasGC = productOfferTypes?.some(offerType =>
										offerType.includes(PRODUCT_TAG_CODES.GC)
									);
									return hasCDC && !hasGC;
								}
								return productOfferTypes?.some(offerType =>
									offerType.startsWith(selectedValue)
								);
							});
						},
						allowSingleOption: true,
						optionMode: OPTION_MODE.MANUAL_ONLY,
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.MULTI_STAGE_JOURNEY,
				facetTitle: formatMessage({ id: "multi.stage.journey" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.MULTI_STAGE_JOURNEY,
						title: formatMessage({ id: "multi.stage.journey" }),
						valueGetter: product => product.productExperience,
						buildOptions: products =>
							[...new Set(products.map(product => product.productExperience))]
								.filter(option =>
									Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(option)
								)
								.map(option => ({
									value: option,
									label: formatMessage({
										id: MULTI_STAGE_JOURNEY_OPTIONS[option],
									}),
								})),
						optionMode: OPTION_MODE.MANUAL_ONLY,
						allowSingleOption: true,
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.TOPIC,
				facetTitle: formatMessage({ id: "listing.filter.theme.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.TOPIC,
						title: formatMessage({ id: "listing.filter.theme.label" }),
						valueGetter: item => item.topics,
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.MONTH,
				facetTitle: formatMessage({ id: "listing.filter.month.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.MONTH,
						title: formatMessage({ id: "listing.filter.month.label" }),
						valueGetter: item => item.months.map(month => month.label),
						formatLabel: ({ label }) => {
							const [year, month] = label?.split("/");
							const date = new Date(Number(year), Number(month) - 1, 1);
							return formatDate(date, { month: "long", year: "numeric" });
						},
						customSort: (a, b) => a?.value?.localeCompare(b?.value),
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.PROPERTY_NAME,
				facetTitle: formatMessage({ id: "product.page.filter.property.name.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.PROPERTY_NAME,
						title: formatMessage({ id: "product.page.filter.property.name.label" }),
						allowSearch: true,
						search: { placeholderKey: "filter.property.name.searchbar.placeholder" },
						valueGetter: product => product.id,
						buildOptions: products =>
							products
								.filter(
									product =>
										!Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(
											product?.productExperience
										)
								)
								.map(product => ({ value: product.id, label: product.name }))
								.sort((a, b) => a.label.localeCompare(b.label)),
						optionMode: OPTION_MODE.MANUAL_ONLY,
						showCount: false,
					},
				],
			},
		],
		[shouldShowMultiStageJourneyOption, formatMessage]
	);

	if (finalProductList.length === 0) {
		return null;
	}

	return (
		<FilterProvider products={finalProductList} facetConfigs={facetConfigs}>
			<SortProvider comparators={CUSTOM_COMPARATORS}>
				<ProductListBlock
					{...props}
					title={title}
					redirectUrl={redirectUrl}
					isMobile={isMobile}
					shouldHideProductPriceBanner={shouldHideProductPriceBanner}
				/>
			</SortProvider>
		</FilterProvider>
	);
};

ProductListBlockContainer.propTypes = {
	merchCode: PropTypes.string,
	shortVersion: PropTypes.bool,
	shortVersionFeaturedProductUris: PropTypes.array,
	displayFilters: PropTypes.bool,
};

export default memo(ProductListBlockContainer);
